import { useState } from "react";
import { ContactFormData, submitContactForm } from "../api/contactForm";
import ReactPixel from "react-facebook-pixel";

/**
 * Хук для использования состояния отправки формы в компонентах React
 * @param initialFormData Начальные данные формы
 * @param endpoint URL эндпоинта API
 * @returns Объект с состоянием и методами для управления формой
 */
export function useContactForm(initialFormData: ContactFormData) {
  const [formData, setFormData] = useState<ContactFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [formStatus, setFormStatus] = useState<{
    type: "success" | "error" | "info" | null;
    message: string | null;
  }>({
    type: null,
    message: null,
  });

  // Обработчик изменения полей формы
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Обработчик отправки формы
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Установка состояния отправки
    setIsSubmitting(true);
    setFormStatus({ type: "info", message: "Отправка формы..." });

    try {
      // Отправка данных формы
      const result = await submitContactForm(formData);

      if (result.success) {
        ReactPixel.track("SubmitForm");
        // Успешная отправка
        setFormStatus({
          type: "success",
          message: result.message || "Форма успешно отправлена!",
        });

        // Очистка формы после успешной отправки
        setFormData(initialFormData);
      } else {
        // Ошибка
        setFormStatus({
          type: "error",
          message: result.error || "Произошла ошибка при отправке формы",
        });
      }
    } catch (error) {
      // Непредвиденная ошибка
      setFormStatus({
        type: "error",
        message: "Непредвиденная ошибка. Пожалуйста, попробуйте позже.",
      });
    } finally {
      // Сброс состояния отправки
      setIsSubmitting(false);
    }
  };

  // Сброс формы в начальное состояние
  const resetForm = () => {
    setFormData(initialFormData);
    setFormStatus({ type: null, message: null });
  };

  return {
    formData,
    setFormData,
    handleChange,
    handleSubmit,
    isSubmitting,
    formStatus,
    resetForm,
  };
}
